import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Box, Button, Flex, FormControl, FormLabel, Heading, Image, Input,
  Select, Spinner, Switch, Text, Textarea, useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { supabase } from '../../lib/supabaseClient';
import { normalizeStorageUrl, pickLocalized } from '../../lib/content';
import LocalizedFields, { LOCALES, emptyLocalized, toLocalized } from './LocalizedFields';
import { MAX_MEDIA_BYTES, removeStoredFile, uploadImage } from './storage';

const isPermissionError = (error) => /row-level security|row level security|permission|unauthorized|policy/i
  .test(`${error?.message || ''} ${error?.error || ''}`);

const EXTRA_FIELD_TYPES = {
  text: Input,
  date: Input,
  select: Select,
  textarea: Textarea,
};

const ItemRow = ({ item, title, badges, onEdit, onTogglePublish, onDelete, deleting, saving }) => {
  const { t } = useTranslation();

  return (
  <Flex
    bg="surface.base"
    border="1px solid"
    borderColor="border.subtle"
    borderRadius="xl"
    p={4}
    mb={3}
    align="center"
    gap={4}
    boxShadow="sm"
  >
    {item.image ? (
      <Image
        src={normalizeStorageUrl(item.image)}
        alt={title}
        boxSize="64px"
        objectFit="cover"
        borderRadius="md"
        flexShrink={0}
      />
    ) : (
      <Box w="64px" h="64px" bgGradient="linear(to-br, brand.500, brand.700)" borderRadius="md" flexShrink={0} />
    )}
    <Box flex="1" minW="0">
      <Flex align="center" gap={2} mb={1}>
        <Heading as="h4" size="sm" noOfLines={1}>
          {title}
        </Heading>
        {!item.is_published && (
          <Badge colorScheme="orange" borderRadius="full" px={3} py={0.5} fontSize="xs">
            {t('admin.draft')}
          </Badge>
        )}
      </Flex>
      <Flex gap={2} flexWrap="wrap">
        {badges}
      </Flex>
    </Box>
    <Flex gap={1} flexShrink={0}>
      <Button
        size="sm"
        variant="ghost"
        colorScheme="brand"
        isLoading={saving}
        onClick={() => onTogglePublish(item)}
      >
        {item.is_published ? t('admin.unpublish') : t('admin.publish')}
      </Button>
      <Button
        leftIcon={<FaEdit />}
        size="sm"
        variant="ghost"
        colorScheme="brand"
        onClick={() => onEdit(item)}
      >
        {''}
      </Button>
      <Button
        leftIcon={<FaTrash />}
        size="sm"
        variant="ghost"
        colorScheme="red"
        isLoading={deleting}
        onClick={() => onDelete(item)}
      >
        {''}
      </Button>
    </Flex>
  </Flex>
  );
};

const ItemManager = ({
  table,
  folder,
  newLabelKey,
  fields = [],
  defaultValues = {},
  requiresImage = true,
  renderBadges = () => null,
}) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: emptyLocalized(),
    description: emptyLocalized(),
    image: null,
    is_published: true,
    ...defaultValues,
  });
  const [file, setFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from(table)
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      toast({
        title: `${t('admin.errorPrefix')}: ${error.message}`,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
    }
    setItems(data || []);
    setLoading(false);
  }, [table, t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm({
      title: emptyLocalized(),
      description: emptyLocalized(),
      image: null,
      is_published: true,
      ...defaultValues,
    });
    setFile(null);
    setRemoveImage(false);
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = toLocalized(form.title);
    if (!LOCALES.some((locale) => (title[locale.code] || '').trim())) {
      toast({
        title: t('admin.titleRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    if (requiresImage && !file && !form.image) {
      toast({
        title: t('admin.imageRequired'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    if (file && file.type && !file.type.startsWith('image/')) {
      toast({
        title: t('admin.invalidImageType'),
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    if (file && file.size > MAX_MEDIA_BYTES) {
      toast({
        title: t('admin.fileTooLarge'),
        status: 'warning',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setSaving(true);
    try {
      let imageUrl = removeImage ? null : form.image;
      if (file) imageUrl = await uploadImage(file, folder);

      const payload = {
        title,
        description: toLocalized(form.description),
        image: imageUrl,
        is_published: form.is_published,
      };
      fields.forEach((field) => {
        payload[field.name] = form[field.name] || null;
      });

      if (editing) {
        const { error } = await supabase.from(table).update(payload).eq('id', editing.id);
        if (error) throw error;
        if (editing.image && editing.image !== imageUrl) {
          await removeStoredFile(editing.image);
        }
        toast({
          title: t('admin.updated'),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      } else {
        const { error } = await supabase.from(table).insert(payload);
        if (error) throw error;
        toast({
          title: t('admin.created'),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top',
        });
      }
      resetForm();
      load();
    } catch (err) {
      toast({
        title: isPermissionError(err) ? t('admin.uploadForbidden') : err.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (item) => {
    const next = {
      title: toLocalized(item.title),
      description: toLocalized(item.description),
      image: item.image || null,
      is_published: item.is_published !== false,
    };
    fields.forEach((field) => {
      next[field.name] = item[field.name] || '';
    });
    setForm(next);
    setFile(null);
    setRemoveImage(false);
    setEditing(item);
    setShowForm(true);
  };

  const handleTogglePublish = async (item) => {
    setTogglingId(item.id);
    const { error } = await supabase
      .from(table)
      .update({ is_published: !item.is_published })
      .eq('id', item.id);
    setTogglingId(null);
    if (error) {
      toast({
        title: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }
    load();
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t('admin.confirmDelete'))) return;
    setDeletingId(item.id);
    const { error } = await supabase.from(table).delete().eq('id', item.id);
    if (error) {
      toast({
        title: error.message,
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      setDeletingId(null);
      return;
    }
    await removeStoredFile(item.image);
    toast({
      title: t('admin.deleted'),
      status: 'success',
      duration: 3000,
      isClosable: true,
      position: 'top',
    });
    setDeletingId(null);
    load();
  };

  const updateExtra = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} gap={3} flexWrap="wrap">
        <Button colorScheme="brand" onClick={() => (showForm ? resetForm() : setShowForm(true))}>
          {showForm ? t('admin.cancel') : t(newLabelKey)}
        </Button>
        {editing && (
          <Text fontSize="sm" color="text.muted">{t('admin.editing')}</Text>
        )}
      </Flex>

      {showForm && (
        <Box
          bg="surface.base"
          border="1px solid"
          borderColor="border.subtle"
          borderRadius="2xl"
          boxShadow="sm"
          p={8}
          mb={8}
        >
          <form onSubmit={handleSubmit}>
            <LocalizedFields
              value={form}
              onChange={(next) => setForm((prev) => ({ ...prev, ...next }))}
            />

            {fields.length > 0 && (
              <Flex gap={5} mb={6} direction={{ base: 'column', md: 'row' }} flexWrap="wrap">
                {fields.map((field) => {
                  const Component = EXTRA_FIELD_TYPES[field.type] || Input;
                  return (
                    <FormControl key={field.name} flex="1" minW={{ base: '100%', md: '220px' }}>
                      <FormLabel fontWeight="600">{t(field.labelKey)}</FormLabel>
                      {field.type === 'select' ? (
                        <Select
                          value={form[field.name] || ''}
                          onChange={(e) => updateExtra(field.name, e.target.value)}
                        >
                          <option value="">{t('admin.notSpecified')}</option>
                          {field.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {t(option.labelKey)}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Component
                          type={field.type === 'textarea' ? undefined : field.type}
                          rows={field.type === 'textarea' ? 3 : undefined}
                          value={form[field.name] || ''}
                          onChange={(e) => updateExtra(field.name, e.target.value)}
                        />
                      )}
                    </FormControl>
                  );
                })}
              </Flex>
            )}

            <FormControl mb={5} isRequired={requiresImage && !form.image}>
              <FormLabel fontWeight="600">{t('admin.image')}</FormLabel>
              <Input
                type="file"
                accept="image/*"
                p={1}
                onChange={(e) => {
                  setFile(e.target.files?.[0] || null);
                  setRemoveImage(false);
                }}
              />
              {file && (
                <Text mt={2} fontSize="sm" color="text.muted">{file.name}</Text>
              )}
              {!file && form.image && (
                <Flex mt={3} align="center" gap={3}>
                  <Image src={normalizeStorageUrl(form.image)} alt="" boxSize="56px" objectFit="cover" borderRadius="md" />
                  <Button size="sm" variant="ghost" colorScheme="red" onClick={() => { setRemoveImage(true); setForm((prev) => ({ ...prev, image: null })); }}>
                    {t('admin.removeImage')}
                  </Button>
                </Flex>
              )}
            </FormControl>

            <FormControl mb={6}>
              <Flex align="center" gap={3}>
                <Switch
                  id={`${table}-published`}
                  isChecked={form.is_published}
                  onChange={(e) => setForm((prev) => ({ ...prev, is_published: e.target.checked }))}
                  colorScheme="green"
                />
                <FormLabel htmlFor={`${table}-published`} mb={0} fontWeight="600">
                  {t('admin.publishImmediately')}
                </FormLabel>
              </Flex>
            </FormControl>

            <Flex gap={3}>
              <Button type="submit" colorScheme="brand" isLoading={saving} loadingText={t('admin.saving')}>
                {editing ? t('admin.update') : t('admin.publish')}
              </Button>
              <Button type="button" variant="ghost" onClick={resetForm}>
                {t('admin.cancel')}
              </Button>
            </Flex>
          </form>
        </Box>
      )}

      {loading ? (
        <Flex justify="center" py={12}>
          <Spinner size="lg" color="brand.500" />
        </Flex>
      ) : items.length === 0 ? (
        <Text color="text.muted" textAlign="center" py={10}>
          {t('admin.noItems')}
        </Text>
      ) : (
        items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            title={pickLocalized(item.title, i18n.language)}
            badges={renderBadges(item)}
            onEdit={startEdit}
            onTogglePublish={handleTogglePublish}
            onDelete={handleDelete}
            deleting={deletingId === item.id}
            saving={togglingId === item.id}
          />
        ))
      )}
    </Box>
  );
};

export default ItemManager;
